<template>
  <teleport to="#modals">
    <vue-resizable
      data-cy="communication-tool-draggable"
      class="z-101"
      drag-selector=".draggable"
      :width="quickActionsOptions.width"
      :height="quickActionsOptions.height"
      :top="quickActionsOptions.top"
      :left="quickActionsOptions.left"
      :active="[]"
      @drag:end="updatePosition"
    >
      <div
        ref="draggable"
        class="draggable flex items-center space-x-1 rounded-full border border-neutral-50/10 bg-primary-1000 px-2 text-white shadow-md"
      >
        <div
          class="border-r-white/10 border-r h-full flex items-center justify-center py-4 pr-2"
        >
          <icon-base
            :icon="IconCardView"
            view-box="0 0 24 24"
            class="flex-none rotate-90 transform cursor-grab"
          />
        </div>
        <div class="flex items-center gap-x-1">
          <div
            v-for="({ component, listeners }, name) in components"
            :key="name"
            class="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 cursor-pointer"
          >
            <component :is="component" :data-cy="name" v-on="listeners ?? {}" />
          </div>
        </div>
        <div
          class="border-l-white/10 border-l h-full flex items-center justify-center pl-1"
        >
          <div
            class="h-8 w-8 rounded-full flex items-center justify-center hover:bg-white/10 cursor-pointer"
          >
            <icon-base
              :icon="IconClose"
              view-box="0 0 18 18"
              data-cy="close-quick-actions"
              class="cursor-pointer text-white/10 hover:text-white"
              @click="isVisible = false"
            />
          </div>
        </div>
      </div>
    </vue-resizable>
  </teleport>
</template>

<script setup lang="ts">
import { ref } from "vue";
import VueResizable from "vue-resizable";

import { storeToRefs } from "pinia";

import { AnalyticsEvent } from "@/enums/analytics";
import { CommunicationToolItemType } from "@/enums/communicationLogs";
import { DEFAULT_QUICK_ACTIONS_OPTIONS } from "@/helpers/constants/deals";
import { useLocalStorageSetting } from "@/hooks/options";
import { useResizable } from "@/hooks/resizable";
import { useAnalyticsStore } from "@/stores/analytics";
import { useCallsStore } from "@/stores/calls";
import useQuickActionsStore from "@/stores/quickActions";

import Call from "@/components/communication/communicationTypes/Call.vue";
import Sms from "@/components/communication/communicationTypes/Sms.vue";
import IconCardView from "@/components/icons/IconCardView.vue";
import IconClose from "@/components/icons/IconClose.vue";
import IconBase from "@/components/ui/IconBase.vue";

import type { CommunicationModalType } from "@/models/communicationLogs";
import type { Component } from "vue";

interface CommunicationToolItemOptions {
  component: Component;
  isCommunicationItem?: boolean;
  listeners?: Record<
    string,
    (event?: MouseEvent, type?: CommunicationModalType) => void
  >;
}

const { trackEvent } = useAnalyticsStore();
const quickActionsStore = useQuickActionsStore();
const callsStore = useCallsStore();

const quickActionsOptions = useLocalStorageSetting(
  "quickActionsOptions",
  DEFAULT_QUICK_ACTIONS_OPTIONS
);

const { isVisible } = storeToRefs(quickActionsStore);
const { activeCallSession } = storeToRefs(callsStore);

const draggable = ref<HTMLElement | null>(null);

const { updatePosition } = useResizable(quickActionsOptions, draggable);

const showCallForm = () => {
  if (activeCallSession.value) {
    return;
  }
  trackEvent(AnalyticsEvent.QuickActionsOpened, {
    action_type: "call",
  });
  quickActionsStore.openCallForm();
};

const showCommunicationModal = (
  e?: MouseEvent,
  type?: CommunicationModalType
) => {
  if (!e?.target || !type) {
    return;
  }
  trackEvent(AnalyticsEvent.QuickActionsOpened, {
    action_type: type,
  });
  quickActionsStore.openCommunicationModal({ type });
};

const components: Record<
  CommunicationToolItemType.call | CommunicationToolItemType.sms,
  CommunicationToolItemOptions
> = {
  [CommunicationToolItemType.call]: {
    component: Call,
    isCommunicationItem: false,
    listeners: {
      "client:call": showCallForm,
    },
  },
  [CommunicationToolItemType.sms]: {
    component: Sms,
    isCommunicationItem: true,
    listeners: {
      "modal:show": showCommunicationModal,
    },
  },
};
</script>
